// src/components/ui/animations.jsx
import React, { useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

/**
 * Variants base
 */
const makeFadeVariants = (reduced) =>
  reduced
    ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.12 } }, exit: { opacity: 0, transition: { duration: 0.12 } } }
    : { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.22, ease: "easeOut" } }, exit: { opacity: 0, y: -8, transition: { duration: 0.18, ease: "easeIn" } } };

const makeStaggerVariants = (reduced, stagger = 0.06, delayChildren = 0.04) =>
  reduced
    ? { hidden: {}, show: { transition: { staggerChildren: 0 } } }
    : { hidden: {}, show: { transition: { staggerChildren: stagger, delayChildren } } };

const makeItemVariants = (reduced, y = 8) =>
  reduced
    ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.14 } } }
    : { hidden: { opacity: 0, y }, show: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } } };

const makeSlideVariants = (reduced, dir = "up", dist = 12) => {
  const map = {
    up:   { hidden: { opacity: reduced ? 0 : 0, y: dist }, show: { opacity: 1, y: 0 } },
    down: { hidden: { opacity: reduced ? 0 : 0, y: -dist }, show: { opacity: 1, y: 0 } },
    left: { hidden: { opacity: reduced ? 0 : 0, x: dist }, show: { opacity: 1, x: 0 } },
    right:{ hidden: { opacity: reduced ? 0 : 0, x: -dist }, show: { opacity: 1, x: 0 } },
  };
  return reduced
    ? { hidden: { opacity: 0 }, show: { opacity: 1, transition: { duration: 0.14 } } }
    : { ...map[dir], exit: { opacity: 0, transition: { duration: 0.14 } }, transition: { duration: 0.22, ease: "easeOut" } };
};

/**
 * Page wrapper
 * - Transición consistente entre vistas
 * - Scroll top opcional al montar
 * - Puedes usarla como <Page as="section">...</Page>
 */
export function Page({
  children,
  className = "",
  as: Tag = "main",
  layoutId,
  scrollTopOnMount = true,
}) {
  const reduced = useReducedMotion();
  const variants = makeFadeVariants(reduced);

  useEffect(() => {
    if (scrollTopOnMount && typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
    }
  }, [scrollTopOnMount, reduced]);

  return (
    <motion.div
      as={Tag}
      variants={variants}
      initial="hidden"
      animate="show"
      exit="exit"
      layoutId={layoutId}
      className={`container mx-auto px-4 ${className}`}
    >
      {children}
    </motion.div>
  );
}

/**
 * AnimatedRoute
 * - Envuelve el contenido de una ruta para transiciones con AnimatePresence.
 * - Usar en el layout principal alrededor del <Outlet/> o {children}.
 */
export function AnimatedRoute({ children, mode = "wait" }) {
  return <AnimatePresence mode={mode}>{children}</AnimatePresence>;
}

/**
 * FadeIn
 * - Envuelve un bloque con fade/slide sutil.
 */
export function FadeIn({ children, className = "", delay = 0, y = 8 }) {
  const reduced = useReducedMotion();
  const item = makeItemVariants(reduced, y);
  return (
    <motion.div
      variants={item}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * FadeInStagger
 * - Padre con stagger + items hijos con FadeInChild
 */
export function FadeInStagger({ children, className = "", stagger = 0.06, delayChildren = 0.04 }) {
  const reduced = useReducedMotion();
  const list = makeStaggerVariants(reduced, stagger, delayChildren);
  return (
    <motion.div
      variants={list}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * FadeInChild
 * - Úsalo dentro de FadeInStagger para cada item.
 */
export function FadeInChild({ children, className = "", y = 8 }) {
  const reduced = useReducedMotion();
  const item = makeItemVariants(reduced, y);
  return (
    <motion.div variants={item} className={className}>
      {children}
    </motion.div>
  );
}

/**
 * SlideIn
 * - Entrada desde una dirección específica (up/down/left/right)
 */
export function SlideIn({ children, dir = "up", className = "", distance = 12, delay = 0 }) {
  const reduced = useReducedMotion();
  const variants = makeSlideVariants(reduced, dir, distance);
  return (
    <motion.div
      variants={variants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay, duration: reduced ? 0.12 : 0.22, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * TapScale
 * - Micro-interacción para botones/cards (presión táctil)
 */
export function TapScale({ children, className = "", scale = 0.98, ...props }) {
  return (
    <motion.div
      whileTap={{ scale }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/**
 * Helpers de texto animado (opcional)
 */
export function Title({ children, className = "" }) {
  return (
    <FadeIn>
      <h1 className={`text-2xl md:text-3xl font-semibold tracking-tight ${className}`}>{children}</h1>
    </FadeIn>
  );
}

export function Subtitle({ children, className = "" }) {
  return (
    <FadeIn y={6}>
      <p className={`text-slate-600 leading-relaxed ${className}`}>{children}</p>
    </FadeIn>
  );
}
